import api from './api';
import { ExpenseCategory } from '../types';

interface ReportPeriod {
  '3m': string;
  '6m': string;
  '1y': string;
  'all': string;
}

export interface SalesSummary {
  monthly: Array<{
    month: string;
    count: number;
    total: number;
  }>;
  profit: {
    total: number;
    byMonth: Array<{
      month: string;
      profit: number;
    }>;
    byVehicleType: Array<{
      vehicleType: string;
      averageProfit: number;
      count: number;
    }>;
  };
}

export interface ExpensesSummary {
  byCategory: Array<{
    category: ExpenseCategory;
    amount: number;
    count: number;
  }>;
  byMonth: Array<{
    month: string;
    amount: number;
  }>;
  totalAmount: number;
  totalCount: number;
}

export interface InventorySummary {
  count: number;
  value: number;
  byBrand: Array<{
    brand: string;
    count: number;
    value: number;
  }>;
}

export interface ReportStats {
  vehiclesInStock: number;
  vehiclesSold: number;
  totalExpenses: number;
  totalProfit: number;
}

export interface ReportData {
  sales: SalesSummary;
  expenses: ExpensesSummary;
  inventory: InventorySummary;
  stats: ReportStats;
}

// Estruturas vazias para inicialização, NÃO são dados demonstrativos
const emptySalesSummary: SalesSummary = {
  monthly: [],
  profit: {
    total: 0,
    byMonth: [],
    byVehicleType: []
  }
};

const emptyExpensesSummary: ExpensesSummary = {
  byCategory: [],
  byMonth: [],
  totalAmount: 0,
  totalCount: 0
};

const emptyInventorySummary: InventorySummary = {
  count: 0,
  value: 0,
  byBrand: []
};

const emptyReportStats: ReportStats = {
  vehiclesInStock: 0,
  vehiclesSold: 0,
  totalExpenses: 0,
  totalProfit: 0
};

const reportService = {
  /**
   * Busca relatórios gerais para o dashboard
   * @param period Período de tempo para os relatórios
   */
  getReportData: async (period: keyof ReportPeriod = 'all'): Promise<ReportData> => {

    const retorno = await api.get('/report-summary');

     return retorno.data

  },

  /**
   * Busca relatórios de despesas para um veículo específico
   * @param vehicleId ID do veículo
   * @param period Período de tempo para os relatórios
   */
  getVehicleExpensesReport: async (
    vehicleId: number,
    period: keyof ReportPeriod = 'all'
  ): Promise<ExpensesSummary> => {
    try {
      // Tentar usar o endpoint de resumo de despesas por veículo
  
      // Se o resumo não estiver disponível, buscar todas as despesas e filtrar
      const response = await api.get(`/expenses`);
      const allExpenses = response.data || [];

      if (!Array.isArray(allExpenses) || allExpenses.length === 0) {
        return emptyExpensesSummary;
      }

      // Filtrar apenas despesas do veículo específico
      const vehicleExpenses = allExpenses.filter((expense: any) =>
        expense.vehicle_id === vehicleId
      );

      if (vehicleExpenses.length === 0) {
        return emptyExpensesSummary;
      }

      // Processar os dados de forma similar ao getExpensesReport
      // Agrupar por categoria e mês
      const categorySummary: Record<string, { amount: number, count: number }> = {};
      const monthSummary: Record<string, number> = {};
      let totalAmount = 0;
      let totalCount = 0;

      vehicleExpenses.forEach((expense: any) => {
        const category = expense.category || 'other';
        let amount = 0;

        // Extrair valor de forma segura
        if (expense.amount !== undefined) {
          if (typeof expense.amount === 'number') {
            amount = expense.amount;
          } else if (typeof expense.amount === 'string') {
            amount = parseFloat(expense.amount.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
          }
        }

        // Atualizar categoria
        if (!categorySummary[category]) {
          categorySummary[category] = { amount: 0, count: 0 };
        }
        categorySummary[category].amount += amount;
        categorySummary[category].count += 1;

        // Atualizar mês
        const date = new Date(expense.date || new Date());
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        if (!monthSummary[monthKey]) {
          monthSummary[monthKey] = 0;
        }
        monthSummary[monthKey] += amount;

        // Atualizar totais
        totalAmount += amount;
        totalCount += 1;
      });

      return {
        byCategory: Object.entries(categorySummary).map(([category, data]) => ({
          category: category as ExpenseCategory,
          amount: parseFloat(data.amount.toFixed(2)),
          count: data.count
        })),
        byMonth: Object.entries(monthSummary).map(([month, amount]) => ({
          month,
          amount: parseFloat(amount.toFixed(2))
        })).sort((a, b) => b.month.localeCompare(a.month)),
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        totalCount
      };
    } catch (error) {
      console.error(`Erro ao buscar relatório de despesas do veículo ${vehicleId}:`, error);
      return emptyExpensesSummary;
    }
  },
};

export default reportService; 